import { motion, AnimatePresence } from 'framer-motion';
import { X, Wifi } from 'lucide-react';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLocalDiscoveryEnabled: boolean;
  onToggleLocalDiscovery: () => void;
}

export function SettingsModal({
  isOpen,
  onClose,
  isLocalDiscoveryEnabled,
  onToggleLocalDiscovery,
}: SettingsModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', duration: 0.3 }}
              className="w-full max-w-md bg-gray-900 rounded-2xl shadow-2xl border border-gray-700 overflow-hidden"
            >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">Settings</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-800 rounded-lg"
                aria-label="Close settings"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Local Discovery Setting */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Wifi size={18} className="text-purple-400" />
                    <h3 className="text-base font-semibold text-white">
                      Local Network Discovery
                    </h3>
                  </div>
                  <p className="text-sm text-gray-400">
                    Automatically discover and connect to devices on the same WiFi network
                  </p>
                </div>
                
                {/* Toggle Switch */}
                <button
                  onClick={onToggleLocalDiscovery}
                  className={`relative ml-4 flex-shrink-0 w-12 h-6 rounded-full transition-colors ${
                    isLocalDiscoveryEnabled ? 'bg-purple-500' : 'bg-gray-600'
                  }`}
                  aria-label={`${isLocalDiscoveryEnabled ? 'Disable' : 'Enable'} local discovery`}
                >
                  <motion.div
                    className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-md"
                    initial={false}
                    animate={{
                      left: isLocalDiscoveryEnabled ? '26px' : '2px',
                    }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                </button>
              </div>

              {/* Info Box */}
              {isLocalDiscoveryEnabled && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4"
                >
                  <p className="text-sm text-purple-300">
                    ✓ Local discovery is enabled. Devices on your network will appear as purple dots on the radar.
                  </p>
                </motion.div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-800/50 border-t border-gray-700">
              <button
                onClick={onClose}
                className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium transition-all"
              >
                Done
              </button>
            </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
